'use client';

import React, { createContext, useContext } from 'react';

export type LocaleLanguage = 'de' | 'en';

const DEFAULT_LOCALE: LocaleLanguage = 'de';

type LocaleContextType = {
  locale: LocaleLanguage;
  dict: any;
};

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

export const LocaleProvider = ({
  children,
  locale,
  dict,
}: {
  children: React.ReactNode;
  locale: LocaleLanguage;
  dict: any;
}) => {
  return (
    <LocaleContext.Provider value={{ locale, dict }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useTranslations = () => {
  const context = useContext(LocaleContext);

  if (!context) {
    console.warn(
      '⚠️ useTranslations called outside of LocaleProvider. Falling back to default values.',
    );

    return {
      locale: DEFAULT_LOCALE,
      dict: {},
      t: (key: string, params?: Record<string, string | number>) => {
        let translation = key;
        if (params) {
          Object.entries(params).forEach(([paramKey, paramValue]) => {
            translation = translation.replace(
              `{${paramKey}}`,
              String(paramValue),
            );
          });
        }
        return translation;
      },
      getSection: <T = any,>(_section: string): T => ({}) as T,
    };
  }

  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation =
      key.split('.').reduce((acc, part) => acc?.[part], context.dict) || key;

    if (params) {
      Object.entries(params).forEach(([paramKey, paramValue]) => {
        translation = translation.replace(`{${paramKey}}`, String(paramValue));
      });
    }
    return translation;
  };

  return {
    locale: context.locale,
    dict: context.dict,
    t,
  };
};
